import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowLeft } from 'lucide-react';
import { v4 as uuid } from 'uuid';
import { capture } from '@/lib/analytics';
import { OrbisMarkSVG } from '@/components/OrbisLogo';
import OrbisSpinner from '@/components/ui/orbis-spinner';
import GlassNav from '@/components/magic/GlassNav';
import CreditMeter from '@/components/CreditMeter';

import CloneReferenceStage from '@/components/clone/CloneReferenceStage';
import CloneBriefStage from '@/components/clone/CloneBriefStage';
import CloneBuildStage from '@/components/clone/CloneBuildStage';
import CloneAgentPanel from '@/components/clone/CloneAgentPanel';
import useCloneBuildStream from '@/hooks/useCloneBuildStream';
import useAnalysisStream from '@/hooks/useAnalysisStream';

const ReviewStage = lazy(() => import('@/components/magic/ReviewStage'));
const DeployStage = lazy(() => import('@/components/magic/DeployStage'));

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { id: 'reference', label: 'Reference' },
  { id: 'brief',     label: 'Brand' },
  { id: 'build',     label: 'Build' },
  { id: 'review',    label: 'Review' },
  { id: 'deploy',    label: 'Deploy' },
];

// Agent message helpers
function agentMsg(text, extra = {}) {
  return { id: uuid(), role: 'agent', text, timestamp: Date.now(), toolCard: null, ...extra };
}
function userMsg(text) {
  return { id: uuid(), role: 'user', text, timestamp: Date.now() };
}

const INTENT_LABELS_MAP = {
  match_visual_style: 'Style Match',
  structure_inspiration: 'Structure Inspiration',
  redesign_reference: 'Redesign',
  create_my_version: 'My Version',
};

function hasDesignSystemContent(value) {
  return Boolean(value && typeof value === 'object' && Object.keys(value).length > 0);
}

function resolveDesignSystem(...candidates) {
  return candidates.find(hasDesignSystemContent) || null;
}

// Client-side intent mapping from freeform text in Reference stage
function mapReferenceIntent(text) {
  const lower = text.toLowerCase();
  if (/\b(exact|identical|same|copy|match|pixel)\b/.test(lower))
    return { cloneIntent: 'match_visual_style', referenceFidelity: 95 };
  if (/\b(premium|luxury|modern|sleek|polish|elevat|upgrad)\b/.test(lower))
    return { cloneIntent: 'redesign_reference', referenceFidelity: 60 };
  if (/\b(layout|structure|sections?|flow|order)\b/.test(lower))
    return { cloneIntent: 'structure_inspiration', referenceFidelity: 50 };
  if (/\b(inspir|loose|different|original|my own|creative)\b/.test(lower))
    return { cloneIntent: 'create_my_version', referenceFidelity: 20 };
  if (/\b(color|colour|palette|brand)\b/.test(lower))
    return { cloneIntent: 'match_visual_style', referenceFidelity: 85 };
  return null; // no match
}

// Client-side brief extraction from freeform text
function extractBriefFromText(text) {
  const result = { company_name: '', industry: '', description: text };
  // "called [Name]" or "[Name] is a..."
  const calledMatch = text.match(/called\s+["']?([A-Z][A-Za-z0-9&\s'.]+?)["']?(?:\s*[,.]|\s+(?:we|that|which|and|in|based)|\s*$)/i);
  const namedMatch = text.match(/^["']?([A-Z][A-Za-z0-9&\s'.]{1,40})["']?\s+is\s+/);
  if (calledMatch) result.company_name = calledMatch[1].trim();
  else if (namedMatch) result.company_name = namedMatch[1].trim();
  else if (text.split(/\s+/).length <= 4 && /^[A-Z]/.test(text)) result.company_name = text.trim();

  const industries = ['accounting', 'legal', 'law', 'tech', 'saas', 'fintech', 'ecommerce',
    'e-commerce', 'healthcare', 'real estate', 'marketing', 'consulting', 'agency',
    'restaurant', 'fitness', 'education', 'construction', 'retail', 'dental', 'plumbing',
    'photography', 'design', 'architecture', 'insurance', 'coaching'];
  const lower = text.toLowerCase();
  for (const ind of industries) {
    if (lower.includes(ind)) { result.industry = ind.charAt(0).toUpperCase() + ind.slice(1); break; }
  }
  return result;
}

export default function CloneModePage() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  // Stage state
  const [viewStage, setViewStage] = useState('reference');
  const [completedStages, setCompletedStages] = useState([]);

  // Flow data
  const [analysis, setAnalysis] = useState(null);
  const [refUrl, setRefUrl] = useState('');
  const [briefData, setBriefData] = useState(null);
  const [generatedHtml, setGeneratedHtml] = useState('');
  const [generatedCss, setGeneratedCss] = useState('');
  const [generatedJs, setGeneratedJs] = useState('');
  const [designSystem, setDesignSystem] = useState(null);

  // Clone intent, fidelity & analysis quality
  const [cloneIntent, setCloneIntent] = useState('match_visual_style');
  const [referenceFidelity, setReferenceFidelity] = useState(72);
  const [analysisStatus, setAnalysisStatus] = useState('complete');
  const [referenceReport, setReferenceReport] = useState(null);

  // Agent panel state
  const [agentMessages, setAgentMessages] = useState([]);
  const [isAgentProcessing, setIsAgentProcessing] = useState(false);
  const loadedRef = useRef(false);  // prevent StrictMode double-mount
  const buildCompleteHandledRef = useRef(false);
  const briefStageRef = useRef(null);
  const buildCommentaryRef = useRef(new Set()); // track which build steps we've commented on

  // Analysis stream — live progress during URL analysis
  const {
    steps: analysisSteps, isAnalyzing: analysisStreaming, isComplete: analysisComplete,
    result: analysisResult, error: analysisError, startAnalysis,
  } = useAnalysisStream(projectId);

  // Build stream — lifted to page level so AgentPanel can read it
  const [buildStarted, setBuildStarted] = useState(false);
  const {
    steps, thinkingMessages, previewHtml, previewCss, previewJs,
    isBuilding, isComplete: buildComplete, error: buildError, result: buildResult, elapsedMs,
    startBuild, abortBuild,
  } = useCloneBuildStream(projectId);

  const addAgentMessage = useCallback((msg) => {
    setAgentMessages(prev => [...prev, msg]);
    // Fire-and-forget save to backend
    try {
      axios.post(`${API_URL}/clone/projects/${projectId}/messages`, {
        role: msg.role, text: msg.text,
        ...(msg.toolCard ? { toolCard: msg.toolCard } : {}),
        ...(msg.suggestions ? { suggestions: msg.suggestions } : {}),
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('orbis_token')}` } }).catch(() => {});
    } catch {}
  }, [projectId]);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    loadProject();
  }, [projectId]);

  const loadProject = async () => {
    try {
      const { data: p } = await axios.get(`${API_URL}/projects/${projectId}`);
      if (p.magic_mode) { navigate(`/magic/${projectId}`, { replace: true }); return; }
      if (!p.clone_mode) { navigate(`/workspace/${projectId}`, { replace: true }); return; }

      setProject(p);
      if (p.reference_url) setRefUrl(p.reference_url);
      if (p.reference_analysis) setAnalysis(p.reference_analysis);

      // Load saved chat messages (persisted across sessions)
      let savedMessages = [];
      try {
        const { data: msgs } = await axios.get(`${API_URL}/clone/projects/${projectId}/messages`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('orbis_token')}` },
        });
        if (msgs?.length) savedMessages = msgs;
      } catch {}

      // Check for generated HTML in both top-level column and magic_state.generated_code
      const ms = p.magic_state || {};
      const existingHtml = p.generated_html || ms.generated_code?.html || '';
      if (existingHtml) {
        setGeneratedHtml(existingHtml);
        setGeneratedCss(p.generated_css || ms.generated_code?.css || '');
        setGeneratedJs(p.generated_js || ms.generated_code?.js || '');
        setDesignSystem(resolveDesignSystem(p.design_system, ms.design_system));
        if (ms.reference_report) setReferenceReport(ms.reference_report);
        setViewStage('review');
        setCompletedStages(['reference', 'brief', 'build']);
        setAgentMessages(savedMessages.length ? savedMessages : [
          agentMsg(`Your ${p.name} website is ready in the review editor. You can ask me to make any changes — colors, content, layout — and I'll apply them live.`),
        ]);
      } else if (p.reference_analysis) {
        setViewStage('brief');
        setCompletedStages(['reference']);
        setAgentMessages(savedMessages.length ? savedMessages : [
          agentMsg("I'm Orbis Agent. I'll guide you through cloning a website and personalizing it with your brand."),
        ]);
      } else {
        setAgentMessages(savedMessages.length ? savedMessages : [
          agentMsg("I'm Orbis Agent. I'll guide you through cloning a website and personalizing it with your brand. Start by entering the reference URL you'd like to clone."),
        ]);
      }
    } catch {
      toast.error('Failed to load project');
      navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Analysis SSE → live tool card in agent panel ──
  const analysisCardIdRef = useRef(null);

  // When SSE stream starts, post a "live" agent message with updating tool card
  useEffect(() => {
    if (!analysisStreaming || analysisCardIdRef.current) return;
    const cardId = uuid();
    analysisCardIdRef.current = cardId;
    addAgentMessage(agentMsg(
      `Analyzing ${refUrl?.replace(/^https?:\/\//, '') || 'the reference'}...`,
      {
        id: cardId,
        isLiveAnalysis: true,
        toolCard: {
          label: 'Analyzing website',
          description: 'Capturing screenshot, extracting design tokens...',
          steps: [],
        },
      }
    ));
  }, [analysisStreaming]);

  // Update the live tool card as SSE steps complete
  useEffect(() => {
    if (!analysisCardIdRef.current || !analysisSteps.length) return;
    setAgentMessages(prev => prev.map(msg => {
      if (msg.id !== analysisCardIdRef.current) return msg;
      const stepLabels = {
        crawl: 'Captured website screenshot',
        colors: `Extracted color palette`,
        fonts: 'Read typography',
        sections: 'Detected layout sections',
        brand_ai: 'AI analyzed design language',
      };
      return {
        ...msg,
        toolCard: {
          ...msg.toolCard,
          label: analysisComplete ? 'Analysis complete' : 'Analyzing website',
          steps: analysisSteps.map(s => ({
            id: s.id,
            label: s.status === 'done' && s.data
              ? (s.id === 'colors' ? `Extracted ${s.data.count} color tokens` :
                 s.id === 'fonts' ? `Found ${s.data.fonts?.length || 0} fonts: ${(s.data.fonts || []).join(', ')}` :
                 s.id === 'sections' ? `Detected ${s.data.count} sections: ${(s.data.sections || []).join(', ')}` :
                 s.id === 'crawl' ? `Screenshot captured (${s.data.images_count} images)` :
                 s.id === 'brand_ai' ? `Vibe: "${(s.data.vibe || '').slice(0, 60)}"` :
                 stepLabels[s.id] || s.label)
              : s.label,
            status: s.status,
            duration_ms: s.duration_ms,
          })),
          isComplete: analysisComplete,
          description: analysisComplete
            ? `${analysisSteps.filter(s => s.status === 'done').length} steps complete`
            : `Step ${analysisSteps.filter(s => s.status === 'done').length + 1} of ${analysisSteps[0]?.total || 5}`,
        },
      };
    }));
  }, [analysisSteps, analysisComplete]);

  // When analysis completes, post the summary + chips and feed result to CloneReferenceStage
  useEffect(() => {
    if (!analysisComplete || !analysisResult) return;
    analysisCardIdRef.current = null;

    // Set analysis state for other components
    setAnalysis(analysisResult);
    setAnalysisStatus(analysisResult.analysis_status || 'complete');

    // Post summary message with chips (same as handleReferenceComplete)
    const sections = analysisResult.layout_sections || [];
    const bgColor = analysisResult.colors?.background || '';
    const fonts = (analysisResult.fonts || []).slice(0, 2);
    const vibe = analysisResult.vibe || 'a design style';
    let themeName = '';
    if (bgColor) {
      try {
        const r = parseInt(bgColor.slice(1, 3), 16), g = parseInt(bgColor.slice(3, 5), 16), b = parseInt(bgColor.slice(5, 7), 16);
        themeName = (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.3 ? 'dark' : 'light';
      } catch {}
    }
    const domain = refUrl?.replace(/^https?:\/\//, '').replace(/\/$/, '') || 'the reference';

    addAgentMessage(agentMsg(
      `Analyzed **${domain}**. ${sections.length}-section ${themeName} theme${fonts.length ? `. Typography: ${fonts.join(' + ')}` : ''}. Vibe: "${vibe}". How would you like to use this reference?`,
      {
        suggestions: [
          { label: 'High fidelity match', action: 'set_strategy', payload: { cloneIntent: 'match_visual_style', referenceFidelity: 85 } },
          { label: 'Keep structure, modernize', action: 'set_strategy', payload: { cloneIntent: 'redesign_reference', referenceFidelity: 45 } },
          { label: 'Loose inspiration only', action: 'set_strategy', payload: { cloneIntent: 'create_my_version', referenceFidelity: 25 } },
        ],
      }
    ));
  }, [analysisComplete, analysisResult]);

  // ── Handle analysis trigger from CloneReferenceStage ──
  const handleAnalyzeStream = useCallback((url) => {
    setRefUrl(url);
    startAnalysis(url);
  }, [startAnalysis]);

  const advanceStage = (stageId) => {
    setCompletedStages(prev => prev.includes(stageId) ? prev : [...prev, stageId]);
    const idx = STAGES.findIndex(s => s.id === stageId);
    if (idx < STAGES.length - 1) setViewStage(STAGES[idx + 1].id);
  };

  const canNavigateTo = (stageId) => {
    const idx = STAGES.findIndex(s => s.id === stageId);
    if (idx === 0) return true;
    return completedStages.includes(STAGES[idx - 1].id);
  };

  // ── Stage handlers ──

  const handleReferenceComplete = (analysisResultFromChild, url, strategyOpts) => {
    // Analysis data may already be set by SSE stream — only update if new data provided
    if (analysisResultFromChild && !analysis) {
      setAnalysis(analysisResultFromChild);
      setAnalysisStatus(analysisResultFromChild.analysis_status || 'complete');
    }
    if (url) setRefUrl(url);
    if (strategyOpts) {
      if (strategyOpts.cloneIntent) setCloneIntent(strategyOpts.cloneIntent);
      if (strategyOpts.referenceFidelity != null) setReferenceFidelity(strategyOpts.referenceFidelity);
    }

    // SSE stream already posted the summary + chips — just advance stage
    capture('clone_reference_scanned', { project_id: projectId, url, vibe: (analysisResultFromChild || analysis)?.vibe });
    advanceStage('reference');

    // Post brief stage greeting
    addAgentMessage(agentMsg(
      'Tell me about your brand. Just describe your business in a sentence and I\'ll fill in the form, or fill it in yourself and hit "Generate Clone".',
      {
        suggestions: [
          { label: 'Build with defaults', action: 'trigger_build', payload: {} },
        ],
      }
    ));
  };

  const handleBriefComplete = (data) => {
    setBriefData(data);
    setBuildStarted(false);
    buildCompleteHandledRef.current = false;
    addAgentMessage(userMsg(`${data.company_name}${data.industry ? ` · ${data.industry}` : ''}${data.description ? ` — ${data.description}` : ''}`));
    addAgentMessage(agentMsg(
      `Got it. I'll build ${data.company_name}'s website now — adapting the reference design to your brand identity with the ${data.model?.split('/').pop() || 'AI'} model.`
    ));
    advanceStage('brief');
    setViewStage('build');

    // Save project name to backend so reload knows brief was completed (Fix 1)
    try {
      axios.patch(`${API_URL}/projects/${projectId}`, {
        name: data.company_name,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('orbis_token')}` } }).catch(() => {});
    } catch {}
  };

  // Auto-start streaming build when entering build stage with briefData
  // Uses the Crawl4AI-powered section-build pipeline (screenshot → layout → sections → assembly)
  useEffect(() => {
    if (viewStage === 'build' && briefData && !buildStarted && !isBuilding) {
      setBuildStarted(true);
      startBuild({
        ...briefData,
        clone_intent: cloneIntent,
        reference_fidelity: referenceFidelity,
        fallback_mode: analysis?.fallback_mode || 'direct-clone',
      });

      addAgentMessage(agentMsg(
        `Building ${briefData.company_name}'s website section by section — watch the live preview as each part comes together.`,
        {
          id: 'build-live-msg',
          isLiveBuild: true,
          toolCard: {
            label: 'Building website',
            description: 'Capturing reference → generating sections...',
            steps: [],
          },
        }
      ));
    }
  }, [viewStage, briefData, buildStarted, isBuilding]);

  // When build completes, add completion message (only once)
  useEffect(() => {
    if (buildComplete && briefData && !buildCompleteHandledRef.current) {
      buildCompleteHandledRef.current = true;
      addAgentMessage(agentMsg(
        `${briefData.company_name} is ready. Click "Review Website" to open the editor — you can tweak colors, fonts, content, or ask me to make specific changes.`
      ));
    }
  }, [buildComplete]);

  // Build stage contextual commentary based on SSE step completions
  useEffect(() => {
    if (viewStage !== 'build' || !briefData) return;
    const doneSteps = steps.filter(s => s.status === 'done');
    const latest = doneSteps[doneSteps.length - 1];
    if (!latest || buildCommentaryRef.current.has(latest.id)) return;
    buildCommentaryRef.current.add(latest.id);

    const commentary = {
      capture_reference: `Captured the reference layout — using it as the blueprint for ${briefData.company_name}.`,
      design_system: 'Design system locked in — your brand colors and typography are ready.',
      generate_layout: 'Layout shell built — header, navigation, and footer are in place.',
    };
    // Batch steps get dynamic commentary
    const msg = commentary[latest.id]
      || (latest.id?.startsWith('batch_') ? `Section batch complete — check the live preview.` : null);
    if (msg) addAgentMessage(agentMsg(msg));
  }, [steps, viewStage, briefData]);

  const handleBuildComplete = (result) => {
    if (result?.html && result.html.length > 100) {
      setGeneratedHtml(result.html);
      setGeneratedCss(result.css || '');
      setGeneratedJs(result.js || '');
      setDesignSystem(resolveDesignSystem(result.design_system, result.magic_state?.design_system));
      if (result.reference_report) setReferenceReport(result.reference_report);
      advanceStage('build');
      setViewStage('review');
    } else {
      // Build produced empty output — stay on build stage, show error
      toast.error('Build produced empty output. Please try again.');
      setBuildStarted(false);
      buildCompleteHandledRef.current = false;
    }
  };

  const handleRetryBuild = () => {
    setBuildStarted(false);
  };

  // ── Suggestion chip handler ──
  const handleSuggestionClick = useCallback((chip) => {
    addAgentMessage(userMsg(chip.label));

    if (chip.action === 'set_strategy') {
      setCloneIntent(chip.payload.cloneIntent);
      setReferenceFidelity(chip.payload.referenceFidelity);
      const label = INTENT_LABELS_MAP[chip.payload.cloneIntent] || chip.payload.cloneIntent;
      addAgentMessage(agentMsg(
        `Strategy set to "${label}" at ${chip.payload.referenceFidelity}% fidelity. Continue to Brand when you're ready.`
      ));
    } else if (chip.action === 'trigger_build') {
      const fields = briefStageRef.current?.getFields?.();
      // Use project name as fallback if no company name entered
      const companyName = fields?.company_name?.trim() || project?.name || 'My Website';
      // Infer industry from reference analysis or URL (Fix 4)
      let inferredIndustry = fields?.industry || '';
      if (!inferredIndustry && analysis) {
        // Try to detect from URL domain
        const domain = (analysis.url || refUrl || '').replace(/^https?:\/\//, '').split('/')[0].replace('www.', '');
        // Use style keywords as industry hint
        const keywords = (analysis.style_keywords || []).join(', ');
        inferredIndustry = keywords || domain || '';
      }
      const briefFields = {
        company_name: companyName,
        industry: inferredIndustry,
        description: fields?.description || `Website inspired by ${(analysis?.url || refUrl || '').replace(/^https?:\/\//, '').split('/')[0]}`,
        model: fields?.model || '',
        premium_mode: false,
        conversion_goal: fields?.conversion_goal || '',
        conversion_goal_custom: fields?.conversion_goal_custom || '',
      };
      handleBriefComplete(briefFields);
    } else if (chip.action === 'noop') {
      addAgentMessage(agentMsg('Take your time. Adjust the form and hit Generate Clone when ready.'));
    }
  }, [addAgentMessage]);

  // ── Agent chat handler — stage-aware intelligence ──
  const handleAgentMessage = async (text) => {
    addAgentMessage(userMsg(text));
    setIsAgentProcessing(true);

    // ── Reference stage: intent mapping from freeform text ──
    if (viewStage === 'reference') {
      if (!analysis) {
        addAgentMessage(agentMsg('Paste a URL above and click "Analyze Website" first. I\'ll tell you what I find.'));
        setIsAgentProcessing(false);
        return;
      }
      const mapped = mapReferenceIntent(text);
      if (mapped) {
        setCloneIntent(mapped.cloneIntent);
        setReferenceFidelity(mapped.referenceFidelity);
        const label = INTENT_LABELS_MAP[mapped.cloneIntent];
        addAgentMessage(agentMsg(
          `Got it — I'll use the reference as "${label}" at ${mapped.referenceFidelity}% fidelity. Continue to Brand when ready.`
        ));
      } else {
        addAgentMessage(agentMsg(
          'Tell me how you want to use this reference — do you want an exact match, a modernized version, or just loose inspiration?',
          {
            suggestions: [
              { label: 'High fidelity match', action: 'set_strategy', payload: { cloneIntent: 'match_visual_style', referenceFidelity: 85 } },
              { label: 'Modernize it', action: 'set_strategy', payload: { cloneIntent: 'redesign_reference', referenceFidelity: 45 } },
              { label: 'Loose inspiration', action: 'set_strategy', payload: { cloneIntent: 'create_my_version', referenceFidelity: 25 } },
            ],
          }
        ));
      }
      setIsAgentProcessing(false);
      return;
    }

    // ── Brief stage: extract company info from natural language ──
    if (viewStage === 'brief') {
      const extracted = extractBriefFromText(text);
      if (extracted.company_name) {
        briefStageRef.current?.setFields?.(extracted);
        addAgentMessage(agentMsg(
          `Filled in: "${extracted.company_name}"${extracted.industry ? ` in ${extracted.industry}` : ''}. Check the form on the right — adjust anything, then hit Generate Clone.`,
          {
            suggestions: [
              { label: 'Looks good, build now', action: 'trigger_build', payload: {} },
              { label: 'Let me adjust first', action: 'noop', payload: {} },
            ],
          }
        ));
      } else {
        // Try backend extraction as fallback
        try {
          const { data } = await axios.post(`${API_URL}/clone/extract-brief`,
            { text },
            { headers: { Authorization: `Bearer ${localStorage.getItem('orbis_token')}` } }
          );
          if (data.company_name) {
            briefStageRef.current?.setFields?.(data);
            addAgentMessage(agentMsg(
              `Filled in: "${data.company_name}"${data.industry ? ` in ${data.industry}` : ''}. Check the form — adjust anything, then Generate Clone.`,
              {
                suggestions: [
                  { label: 'Looks good, build now', action: 'trigger_build', payload: {} },
                  { label: 'Let me adjust first', action: 'noop', payload: {} },
                ],
              }
            ));
          } else {
            addAgentMessage(agentMsg('I need a company name to get started. Try something like "We\'re a design agency called PixelCraft Studio" or just type the company name.'));
          }
        } catch {
          addAgentMessage(agentMsg('Tell me your company name and I\'ll fill in the form. For example: "Smith & Co, an accounting firm in Dallas."'));
        }
      }
      setIsAgentProcessing(false);
      return;
    }

    // ── Build stage: informational only ──
    if (viewStage === 'build') {
      addAgentMessage(agentMsg('The build is underway — watch the progress above. I\'ll be ready to make edits once the site is generated.'));
      setIsAgentProcessing(false);
      return;
    }

    // ── Review stage: real AI chat (existing behavior) ──
    if (viewStage !== 'review') {
      addAgentMessage(agentMsg('Continue through the workflow to reach the Review stage where I can make live edits.'));
      setIsAgentProcessing(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/clone/projects/${projectId}/chat`,
        { message: text, page_slug: 'home' },
        { headers: { Authorization: `Bearer ${localStorage.getItem('orbis_token')}` } }
      );
      const { type, reply, html, css, js } = response.data;

      if (type === 'edit' && html) {
        setGeneratedHtml(html);
        if (css) setGeneratedCss(css);
        if (js) setGeneratedJs(js);

        addAgentMessage(agentMsg(reply || 'Done — applied the change to your site.', {
          toolCard: {
            label: 'Edited',
            description: text.slice(0, 60),
            isComplete: true,
            steps: [
              { id: 'read', label: 'Read current HTML', status: 'done' },
              { id: 'edit', label: 'Applied change', status: 'done' },
              { id: 'save', label: 'Saved to project', status: 'done' },
            ],
          },
        }));
      } else {
        addAgentMessage(agentMsg(reply || "I've noted that. Let me know if you'd like me to make any edits."));
      }
    } catch (e) {
      addAgentMessage(agentMsg("I couldn't apply that change. Try being more specific, like 'change the hero background to dark navy'."));
    } finally {
      setIsAgentProcessing(false);
    }
  };

  // ── Review stage sync ──
  const handleEditSync = (newState) => {
    if (newState?.generated_code?.html) setGeneratedHtml(newState.generated_code.html);
    if (newState?.generated_code?.css) setGeneratedCss(newState.generated_code.css);
    if (newState?.generated_code?.js) setGeneratedJs(newState.generated_code.js);
    setDesignSystem((prev) => resolveDesignSystem(newState?.design_system, newState?.magic_state?.design_system, prev));
    if (newState?.reference_report) setReferenceReport(newState.reference_report);
  };

  const handleDownload = async () => {
    try {
      const r = await axios.get(`${API_URL}/projects/${projectId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${project?.name || 'clone'}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleApprove = () => {
    capture('clone_approved', { project_id: projectId });
    advanceStage('review');
    setViewStage('deploy');
  };

  const handleDeploy = async () => {
    // Refresh project data after publish so subdomain_slug is populated
    try {
      const { data: p } = await axios.get(`${API_URL}/projects/${projectId}`);
      setProject(p);
    } catch { /* non-critical — deploy succeeded, refresh failed */ }
  };

  const generatedPages = generatedHtml
    ? [{ slug: 'home', name: 'Home', html: generatedHtml, css: generatedCss, js: generatedJs }]
    : [];
  const generatedCode = { html: generatedHtml, css: generatedCss, js: generatedJs };

  // Build state passed to agent panel
  const buildState = {
    steps,
    thinkingMessages,
    isBuilding,
    isComplete: buildComplete,
    elapsedMs,
  };

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--orbis-bg)' }}>
      <OrbisSpinner size={64} />
    </div>
  );

  return (
    <div className="h-screen flex flex-col" style={{ background: 'var(--orbis-bg)' }}>

      {/* ── Navbar — matches Magic Mode header ── */}
      <header
        className="sticky top-0 z-50 flex items-center justify-center"
        style={{ height: '64px', padding: '0 20px', background: 'var(--surface-0)' }}
      >
        {/* Left: Breadcrumb */}
        <div className="absolute top-1/2 -translate-y-1/2 flex items-center gap-2.5" style={{ left: 20 }}>
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-1.5 hover:opacity-80 transition-opacity cursor-pointer group"
            style={{ background: 'none', border: 'none', padding: 0 }}
            title="Back to Projects"
          >
            <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" style={{ color: 'var(--orbis-t3)' }} />
            <OrbisMarkSVG size={20} />
            <span style={{ fontSize: '14px', fontWeight: 400, color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-font)' }}>Projects</span>
          </button>
          <span style={{ fontSize: '14px', color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)' }}>/</span>
          <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}>{project?.name || 'Project'}</span>
        </div>

        {/* Center: GlassNav with clone stages */}
        <GlassNav
          stages={STAGES}
          viewStage={viewStage}
          currentStage={viewStage}
          completedStages={completedStages}
          onStageClick={(id) => canNavigateTo(id) && setViewStage(id)}
          disabled={isProcessing}
        />

        {/* Right: Credits + step counter + badge */}
        <div className="absolute top-1/2 -translate-y-1/2 flex items-center gap-3" style={{ right: 20 }}>
          <CreditMeter compact />
          <span style={{ fontSize: '11px', fontWeight: 400, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)', letterSpacing: '0.5px' }}>
            Step {STAGES.findIndex(s => s.id === viewStage) + 1} of {STAGES.length}
          </span>
          <span style={{
            fontSize: '9px', fontFamily: 'var(--orbis-mono)', fontWeight: 500,
            textTransform: 'uppercase', letterSpacing: '1px',
            padding: '4px 10px', borderRadius: '100px',
            background: 'var(--orbis-s2)', border: '0.5px solid var(--orbis-s3)', color: 'var(--orbis-t2)',
          }}>
            Clone Mode
          </span>
        </div>
      </header>

      {/* ── Main: Agent Panel + Stage Content ── */}
      <div className="flex-1 flex overflow-hidden min-h-0">

        {/* Agent panel — visible in reference/brief/build stages; hidden in review/deploy */}
        {viewStage !== 'review' && viewStage !== 'deploy' && (
          <CloneAgentPanel
            messages={agentMessages}
            buildState={viewStage === 'build' ? buildState : null}
            currentStage={viewStage}
            isProcessing={isAgentProcessing}
            onSendMessage={handleAgentMessage}
            onSuggestionClick={handleSuggestionClick}
            cloneIntent={cloneIntent}
            referenceFidelity={referenceFidelity}
            analysisStatus={analysisStatus}
          />
        )}

        {/* Stage content */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">

          {viewStage === 'reference' && (
            <CloneReferenceStage
              projectId={projectId}
              initialUrl={refUrl}
              initialAnalysis={analysis}
              onComplete={handleReferenceComplete}
              onAnalyze={handleAnalyzeStream}
              analysisFromStream={analysisResult}
            />
          )}

          {viewStage === 'brief' && (
            <CloneBriefStage
              ref={briefStageRef}
              projectId={projectId}
              analysis={analysis}
              initialData={briefData || { company_name: project?.name || '' }}
              isProcessing={isProcessing}
              onComplete={handleBriefComplete}
            />
          )}

          {viewStage === 'build' && (
            briefData
              ? <CloneBuildStage
                  previewHtml={previewHtml}
                  previewCss={previewCss}
                  previewJs={previewJs}
                  analysis={analysis}
                  isComplete={buildComplete}
                  isBuilding={isBuilding}
                  error={buildError}
                  result={buildResult}
                  steps={steps}
                  elapsedMs={elapsedMs}
                  onComplete={handleBuildComplete}
                  onRetry={handleRetryBuild}
                  onCancel={abortBuild}
                  cloneIntent={cloneIntent}
                  referenceFidelity={referenceFidelity}
                  analysisStatus={analysisStatus}
                />
              : <div className="flex-1 flex items-center justify-center" style={{ background: 'var(--orbis-bg)' }}>
                  <div className="text-center">
                    <p style={{ fontSize: '14px', fontWeight: 300, color: '#8A8882', marginBottom: '16px' }}>Brand details required before building.</p>
                    <button
                      onClick={() => setViewStage('brief')}
                      style={{ height: '40px', padding: '0 20px', borderRadius: '100px', background: 'var(--orbis-blue)', border: 'none', fontSize: '13px', fontWeight: 400, color: 'var(--orbis-bg)', cursor: 'pointer' }}
                    >
                      Go to Brand Stage
                    </button>
                  </div>
                </div>
          )}

          {viewStage === 'review' && generatedHtml && (
            <Suspense fallback={
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#8A8882' }} />
              </div>
            }>
              <ReviewStage
                code={generatedCode}
                designSystem={designSystem}
                generationMode="landing_page"
                generatedPages={generatedPages}
                projectId={projectId}
                onApprove={handleApprove}
                onDownload={handleDownload}
                onEdit={handleEditSync}
                isProcessing={isProcessing}
                referenceReport={referenceReport}
                mode="clone"
                modeContext={{
                  referenceReport,
                  cloneIntent,
                  referenceFidelity,
                }}
                reviewCapabilities={{
                  supportsReferenceContext: true,
                }}
              />
            </Suspense>
          )}

          {viewStage === 'deploy' && (
            <Suspense fallback={
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#8A8882' }} />
              </div>
            }>
              <DeployStage
                project={project}
                onDownload={handleDownload}
                onDeploy={handleDeploy}
              />
            </Suspense>
          )}
        </div>
      </div>
    </div>
  );
}
